import { useMemo, useState } from 'react';
import Drawer from '../../../../../components/Drawer';
import ChevronDownIcon from '../../../../../assets/icons/chevron-down.svg?react';

import useJurisdiction from '../../../../../hooks/useJurisdiction';
import { isFromScratch } from '../../data';
import Button from '../../../../../components/Button';
import OutlinedNavigateIcon from '../../../../../assets/icons/navigate-outlined.svg?react';
import { useNavigate } from 'react-router-dom';
import { checkIsLockedAutomation } from '../../../../../utils/lockAutomation';
import { cloneDeep } from 'lodash';
import {
  TableContainer,
  Table,
  TableHeader,
  TableBody,
  TableItem,
  SpaceBetweenBox,
  ActionBox
} from './style';
import { REPORT_NAMINGS, REPORT_TYPES } from '../../../../../consts/reportTypes';

export const ReadonlyJurisdictionDrawer = ({
  isOpen,
  onClose,
  periodId,
  reportType,
  workflowId,
  isLatestPeriod,
  selectedPeriod,
  handleOpenJurisdictionDrawer,
  currentUser
}) => {
  const isScratch = isFromScratch(selectedPeriod);
  const navigate = useNavigate();

  const { jurisdictions } = useJurisdiction({
    onClose,
    periodId,
    projectId: workflowId,
    enabledFetch: isOpen
  });

  const filteredJurisdictions = useMemo(() => {
    if (!isOpen) return [];

    // Earlier this filtered out AIFs not marked `is_included` and dropped
    // jurisdictions whose remaining AIF list was empty. That made sense when
    // the drawer only opened for periods past validation — every AIF had an
    // is_included decision. Now the dashboard opens this drawer for any
    // period that has jurisdictions, including in-progress ones where no AIF
    // is flagged yet; filtering on is_included left the drawer blank. Show
    // every AIF; the editor page is where users toggle inclusion.
    return cloneDeep(jurisdictions) ?? [];
  }, [jurisdictions, isOpen]);

  const isLockedPeriod = checkIsLockedAutomation(selectedPeriod, currentUser);

  const onClickEdit = () => {
    if (reportType === REPORT_TYPES.CONVERSION) {
      handleOpenJurisdictionDrawer();
      return;
    }

    if (isScratch) {
      // Jurisdictions for scratch lives on the dedicated page now (was the
      // inline drawer on the Funds page before the refactor).
      navigate(
        `/project/${selectedPeriod.workflow_id}/jurisdictions?periodId=${selectedPeriod.id}`
      );
      return;
    }

    handleOpenJurisdictionDrawer();
  };

  const jurisdictionNaming = REPORT_NAMINGS.JURISDICTION[reportType] || 'Jurisdiction';

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={jurisdictionNaming}
      style={{
        width: 568
      }}
    >
      <TableContainer>
        {filteredJurisdictions?.map(juris => (
          <JurisdictionTable
            key={juris.id}
            aifm={juris.country_name}
            aifs={juris.national_codes}
            isFCA={juris.reporting_member_state === 'UK'}
          />
        ))}
      </TableContainer>

      {!isLockedPeriod && isLatestPeriod && (
        <ActionBox>
          <Button
            size="sm"
            variant="outlined"
            rightIcon={<OutlinedNavigateIcon />}
            onClick={onClickEdit}
          >
            Open Edit {jurisdictionNaming}’s Page
          </Button>
        </ActionBox>
      )}
    </Drawer>
  );
};

const JurisdictionTable = ({ isFCA, aifm, aifs }) => {
  const [isOpen, setIsOpen] = useState(false);

  const aifNames = useMemo(() => {
    return aifs.map(aif => aif.fund_name);
  }, [aifs]);

  return (
    <Table>
      <TableHeader>
        <SpaceBetweenBox>
          <p>{isFCA ? 'FCA' : 'ESMA'} jurisdictions</p>
          <span>1 jurisdiction / {aifs?.length} funds</span>
        </SpaceBetweenBox>
      </TableHeader>

      <TableBody>
        <TableItem>
          <div className="toggler">
            <button onClick={() => setIsOpen(prev => !prev)}>
              <ChevronDownIcon
                style={{
                  transform: isOpen && 'rotate(180deg)'
                }}
              />
              <span>{aifm}</span>
            </button>
          </div>
          {!isOpen && (
            <div className="aifs">
              <div className="names">{aifNames.join(',')}</div>
            </div>
          )}
        </TableItem>

        {isOpen &&
          aifNames?.map((aif, index) => (
            <TableItem key={index}>
              <div className="aif">{aif}</div>
            </TableItem>
          ))}
      </TableBody>
    </Table>
  );
};
